import {ChevronDown, ChevronUp, MinusCircle} from 'lucide-react'
import React, {FC, useEffect, useId, useState} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'

import {SkeletonBlock, TextSkeleton} from './AllocationSkeleton'

type Props = {
  open: boolean
  title: string
  /** Paragraphs, in order. */
  messages: string[]
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
  /** Sections the removal touches, shown behind "Show affected sections". */
  affected?: string[]
  /** Server flow: the removal preview is loading — text skeleton, Confirm disabled. */
  loading?: boolean
  /** Server flow: the confirmed change is being saved — Confirm shows a spinner. */
  busy?: boolean
}

/**
 * Confirmation for every removal in Teacher Allocation (handover, 3C): nothing changes until the
 * user confirms; Cancel leaves the data as it was. Names and scope are filled in by the caller,
 * and a removal that touches several sections lists them.
 */
const AllocationConfirmDialog: FC<Props> = ({
  open,
  title,
  messages,
  confirmLabel,
  onConfirm,
  onCancel,
  affected,
  loading,
  busy,
}) => {
  const intl = useIntl()
  const titleId = useId()
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (!open) {
      setExpanded(false)
      return
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null

  return createPortal(
    <div className='ta-dialog-root'>
      <div className='ta-dialog-backdrop' onClick={onCancel} aria-hidden />
      <div className='ta-dialog' role='alertdialog' aria-modal='true' aria-labelledby={titleId}>
        <div className='ta-dialog__head'>
          <span className='ta-dialog__icon'>
            <MinusCircle size={20} aria-hidden />
          </span>
          <h2 id={titleId}>{loading ? <SkeletonBlock width={260} height={20} /> : title}</h2>
        </div>
        {loading ? (
          <div className='ta-dialog__text'>
            <TextSkeleton lines={3} />
          </div>
        ) : (
          messages.map((message, idx) => (
            <p key={idx} className='ta-dialog__text'>
              {message}
            </p>
          ))
        )}
        {affected && affected.length > 0 && (
          <div className='ta-dialog__affected'>
            <button
              type='button'
              className='ta-link'
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? (
                <ChevronUp size={16} aria-hidden />
              ) : (
                <ChevronDown size={16} aria-hidden />
              )}
              {intl.formatMessage(
                {
                  id: expanded
                    ? 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.REMOVE.HIDE_AFFECTED'
                    : 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.REMOVE.SHOW_AFFECTED',
                },
                {count: affected.length}
              )}
            </button>
            {expanded && (
              <ul className='ta-dialog__list'>
                {affected.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className='ta-dialog__actions'>
          <button type='button' className='ta-btn ta-btn--ghost' onClick={onCancel} autoFocus>
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.CANCEL'})}
          </button>
          <button
            type='button'
            className='ta-btn ta-btn--danger'
            onClick={onConfirm}
            disabled={loading || busy}
          >
            {busy && <span className='spinner-border spinner-border-sm' aria-hidden />}
            {loading ? <SkeletonBlock width={120} height={14} /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default AllocationConfirmDialog
